import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import AuthService from '../services/AuthService';

const SessionContext = createContext(null);

/**
 * Who is signed in. status: "loading" | "authed" | "anon".
 * The session cookie is shared with the other Orka apps, so a sign-in elsewhere shows up here.
 */
export function SessionProvider({ children }) {
  const [status, setStatus] = useState('loading');
  const [me, setMe] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const data = await AuthService.me();
      setMe(data);
      setStatus('authed');
      setLoadError(null);
    } catch (err) {
      setMe(null);
      if (err.status === 401) {
        setStatus('anon');
      } else {
        // Server unreachable or failing: do not pretend the user signed out.
        setLoadError(err);
        setStatus('anon');
      }
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const setSignedIn = useCallback((data) => {
    setMe(data);
    setStatus('authed');
    setLoadError(null);
  }, []);

  const signOut = useCallback(async () => {
    try { await AuthService.signout(); } catch (e) { /* session may already be gone */ }
    setMe(null);
    setStatus('anon');
  }, []);

  const value = useMemo(() => ({
    status, me, loadError, refresh, setSignedIn, signOut,
    isOwner: Boolean(me?.isOwner),
    allowed: Boolean(me?.access?.allowed),
  }), [status, me, loadError, refresh, setSignedIn, signOut]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside SessionProvider');
  return ctx;
}
